import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';

interface SmartLinkForm {
  name: string;
  targetUrl: string;
  campaign?: string;
  source?: string;
  medium?: string;
}

interface SmartLink {
  id: string;
  name: string;
  targetUrl: string;
  campaign?: string;
  source?: string;
  medium?: string;
}

interface SmartLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SmartLinkForm) => Promise<void>;
  editData?: SmartLink | null;
  loading?: boolean;
}

export default function SmartLinkModal({
  isOpen,
  onClose,
  onSubmit,
  editData,
  loading,
}: SmartLinkModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SmartLinkForm>({
    defaultValues: editData || {
      name: '',
      targetUrl: '',
      campaign: '',
      source: '',
      medium: '',
    },
  });

  const handleFormSubmit = async (data: SmartLinkForm) => {
    await onSubmit(data);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[var(--bg-secondary)]0 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-[var(--bg-secondary)] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-muted)]"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-[var(--text-primary)]">
                      {editData ? 'Edit Smart Link' : 'Create Smart Link'}
                    </Dialog.Title>

                    <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-6 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[var(--text-secondary)]">
                          Link Name *
                        </label>
                        <input
                          {...register('name', { required: 'Name is required' })}
                          type="text"
                          placeholder="e.g., Facebook Campaign"
                          className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                        />
                        {errors.name && (
                          <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[var(--text-secondary)]">
                          Target URL *
                        </label>
                        <input
                          {...register('targetUrl', {
                            required: 'Target URL is required',
                            pattern: {
                              value: /^https?:\/\/.+/,
                              message: 'Must be a valid URL starting with http:// or https://',
                            },
                          })}
                          type="url"
                          placeholder="https://example.com/landing-page"
                          className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                        />
                        {errors.targetUrl && (
                          <p className="mt-1 text-xs text-red-500">{errors.targetUrl.message}</p>
                        )}
                      </div>

                      <div className="border-t border-[var(--border-color)] pt-4">
                        <p className="text-sm font-medium text-[var(--text-secondary)] mb-3">
                          UTM Parameters (Optional)
                        </p>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                          <div>
                            <label className="block text-xs font-medium text-[var(--text-muted)]">
                              Campaign
                            </label>
                            <input
                              {...register('campaign')}
                              type="text"
                              placeholder="summer_promo"
                              className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-[var(--text-muted)]">
                              Source
                            </label>
                            <input
                              {...register('source')}
                              type="text"
                              placeholder="facebook"
                              className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-[var(--text-muted)]">
                              Medium
                            </label>
                            <input
                              {...register('medium')}
                              type="text"
                              placeholder="cpc"
                              className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                        >
                          {loading ? 'Saving...' : editData ? 'Update Link' : 'Create Link'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
